<script setup>
import { computed, inject } from 'vue'

import ButtonComponent from '@/Components/ButtonComponent.vue'
import SvgVue from '@/Components/SvgVue.vue'
// eslint-disable-next-line import/extensions
import { trans } from '@/Translate'

import useCart from '../useCart.js'

const emit = defineEmits(['removeDiscount'])

const cart = useCart(inject('cart'))
const cartModel = cart.cart
const checkoutOptions = inject('checkoutOptions')

const props = defineProps({
  selectedPaymentMethod: String,
  country: String,
})

const activeDiscount = computed(() => {
  return cartModel.value.discount || null
})
const discountDescription = computed(() => {
  if (!activeDiscount.value) {
    return ''
  }

  if (activeDiscount.value.duration === 'repeating') {
    if (cart.priceModel.value.renewal_interval_unit === 'year') {
      let years = Math.ceil(activeDiscount.value.duration_in_months / 12)

      // translators: The discount applies yearly
      return trans('lemonsqueezy_checkout.discount.applies_yearly', {
        years,
      })
    } else {
      // translators: The discount applies monthly
      return trans('lemonsqueezy_checkout.discount.applies_monthly', {
        months: activeDiscount.value.duration_in_months,
      })
    }
  }

  // translators: If the discount is not repeating, explain that the discount applies once
  return trans('lemonsqueezy_checkout.discount.applies_once', { duration: activeDiscount.value.duration })
})
</script>

<template>
  <div>
    <div
      v-if="!cart.isLeadMagnetOrOneHundredPercentDiscount() && !cart.isSubscription()"
      v-show="props.selectedPaymentMethod === 'paypal'"
    >
      <div
        v-if="cartModel.currency !== 'USD' && !cart.isLeadMagnetOrOneHundredPercentDiscount()"
        class="px-3 text-center text-13 text-balance text-grey"
        :class="{
          'mt-2': checkoutOptions.embed,
          'mt-4': !checkoutOptions.embed,
        }"
      >
        {{
          /* translators: Charge description. Explains to the user how much they will be charged */
          $t('lemonsqueezy_checkout.charge_description', {
            price: formatCurrency(cartModel.total_in_usd, 'USD'),
          })
        }}
      </div>
    </div>
    <div
      v-if="cart.isSubscription() && cart.emailIsConfirmed() && checkoutOptions.subscription_preview"
      class="px-3 text-center text-13 text-balance text-grey"
      :class="{
        'mt-2': checkoutOptions.embed,
        'mt-4': !checkoutOptions.embed,
      }"
    >
      {{ cartModel.charge_description }}

      <!-- nosemgrep. input doesn't contain user-generated HTML. -->
      <p v-if="discountDescription && !checkoutOptions.embed" v-html="discountDescription" />
    </div>
    <template v-if="checkoutOptions.embed">
      <div v-if="activeDiscount" class="mt-2 flex items-center justify-center text-13 text-grey">
        <template v-if="cart.isUsageBased()">{{
          /* translators: Shown when a valid discount was applied to the checkout */
          $t('lemonsqueezy_checkout.discount.applied')
        }}</template>
        <template v-else>
          {{
            /* translators: Shown when a valid discount was applied to the checkout, it includes discount value */
            $t('lemonsqueezy_checkout.discount.applied_with_value', {
              discountAmount: formatCurrency(cartModel.discount_total, cartModel.currency),
            })
          }}
        </template>
        <ButtonComponent
          v-if="checkoutOptions.discount"
          variant="plain"
          dusk="checkout-form-remove-discount"
          @click="emit('removeDiscount')"
        >
          <SvgVue icon="Cross Small" class="h-3 w-3 stroke-current" />
        </ButtonComponent>
      </div>
      <div v-if="cart.isSubscription() && discountDescription" class="text-center text-13 text-grey">
        <!-- nosemgrep. input doesn't contain user-generated HTML. -->
        <span v-html="discountDescription" />
      </div>
    </template>
    <div
      v-if="
        checkoutOptions.embed &&
        cartModel.tax &&
        !cart.isSubscription() &&
        !cart.isLeadMagnetOrOneHundredPercentDiscount()
      "
      class="mx-2 text-center text-13 text-grey"
      :class="{ 'mt-3': !activeDiscount, 'mt-1': activeDiscount }"
    >
      {{
        /* translators: Explains that the payment is tax-inclusive. For example "Payment includes $3 for UK VAT" */
        $t('lemonsqueezy_checkout.tax_amount_included', {
          taxAmount: formatCurrency(cartModel.tax, cartModel.currency),
          country: props.country,
          taxName: cartModel.tax_name,
        })
      }}
    </div>
  </div>
</template>
